import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export interface AdaptiveItem {
  exposure_id: string;
  item_version_id: string;
  response_type: string;
  assessment_role: string;
  prompt: string;
  options: { key: string; text: string | null }[];
}

export interface ResponseRendererProps {
  item: AdaptiveItem;
  disabled: boolean;
  onSubmit: (payload: Record<string, unknown>) => void;
}

const optionClass = (selected: boolean) =>
  cn(
    "w-full rounded-xl border p-4 text-left transition-all",
    selected
      ? "border-primary/70 bg-primary/10"
      : "border-border/60 bg-card/60 hover:border-primary/40"
  );

function SelectOne({ item, disabled, onSubmit }: ResponseRendererProps) {
  const [selected, setSelected] = useState<string | null>(null);
  return (
    <div className="space-y-3">
      {item.options.filter(o => o.text).map(o => (
        <button
          key={o.key}
          type="button"
          disabled={disabled}
          aria-pressed={selected === o.key}
          onClick={() => setSelected(o.key)}
          className={optionClass(selected === o.key)}
        >
          <span className="font-heading text-xs text-muted-foreground mr-2">{o.key}</span>
          <span className="font-body text-sm text-foreground/90">{o.text}</span>
        </button>
      ))}
      <Button className="w-full" disabled={disabled || !selected} onClick={() => onSubmit({ selected })}>
        Submit answer
      </Button>
    </div>
  );
}

function SelectMany({ item, disabled, onSubmit }: ResponseRendererProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const toggle = (k: string) =>
    setSelected(prev => (prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]));
  return (
    <div className="space-y-3">
      {item.options.filter(o => o.text).map(o => (
        <button
          key={o.key}
          type="button"
          disabled={disabled}
          aria-pressed={selected.includes(o.key)}
          onClick={() => toggle(o.key)}
          className={optionClass(selected.includes(o.key))}
        >
          <span className="font-heading text-xs text-muted-foreground mr-2">{o.key}</span>
          <span className="font-body text-sm text-foreground/90">{o.text}</span>
        </button>
      ))}
      <Button className="w-full" disabled={disabled || selected.length === 0} onClick={() => onSubmit({ selected })}>
        Submit answer
      </Button>
    </div>
  );
}

function NumericResponse({ disabled, onSubmit }: ResponseRendererProps) {
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("");
  return (
    <div className="space-y-3">
      <Input
        inputMode="decimal"
        aria-label="Your numeric answer"
        placeholder="Your answer"
        value={value}
        disabled={disabled}
        onChange={e => setValue(e.target.value)}
      />
      <Input
        aria-label="Unit (optional)"
        placeholder="Unit (optional)"
        value={unit}
        disabled={disabled}
        onChange={e => setUnit(e.target.value)}
      />
      <Button className="w-full" disabled={disabled || value.trim() === ""} onClick={() => onSubmit({ value, unit })}>
        Submit answer
      </Button>
    </div>
  );
}

function OrderingResponse({ item, disabled, onSubmit }: ResponseRendererProps) {
  const [order, setOrder] = useState(item.options.filter(o => o.text).map(o => o.key));
  const move = (i: number, delta: number) => {
    const next = [...order];
    const j = i + delta;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
  };
  return (
    <div className="space-y-3">
      {order.map((key, i) => {
        const opt = item.options.find(o => o.key === key);
        return (
          <div key={key} className="flex items-center gap-2 rounded-xl border border-border/60 bg-card/60 p-3">
            <span className="font-heading text-xs text-muted-foreground">{i + 1}</span>
            <span className="flex-1 font-body text-sm text-foreground/90">{opt?.text}</span>
            <Button size="sm" variant="ghost" aria-label="Move up" disabled={disabled || i === 0} onClick={() => move(i, -1)}>↑</Button>
            <Button size="sm" variant="ghost" aria-label="Move down" disabled={disabled || i === order.length - 1} onClick={() => move(i, 1)}>↓</Button>
          </div>
        );
      })}
      <Button className="w-full" disabled={disabled} onClick={() => onSubmit({ order })}>
        Submit order
      </Button>
    </div>
  );
}

function PairingResponse({ item, disabled, onSubmit }: ResponseRendererProps) {
  const [pairs, setPairs] = useState<Record<string, string>>({});
  const targets = item.options.filter(o => o.text);
  return (
    <div className="space-y-3">
      {targets.map(o => (
        <div key={o.key} className="rounded-xl border border-border/60 bg-card/60 p-3 space-y-2">
          <p className="font-body text-sm text-foreground/90">{o.text}</p>
          <Input
            aria-label={`Match for ${o.text}`}
            placeholder="Your match"
            disabled={disabled}
            value={pairs[o.key] ?? ""}
            onChange={e => setPairs(p => ({ ...p, [o.key]: e.target.value }))}
          />
        </div>
      ))}
      <Button
        className="w-full"
        disabled={disabled}
        onClick={() => onSubmit({ pairs: targets.map(o => ({ left: o.key, right: pairs[o.key] ?? "" })) })}
      >
        Submit matches
      </Button>
    </div>
  );
}

function WrittenResponse({ disabled, onSubmit }: ResponseRendererProps) {
  const [text, setText] = useState("");
  return (
    <div className="space-y-3">
      <Textarea
        aria-label="Your written answer"
        placeholder="Explain your thinking"
        rows={5}
        value={text}
        disabled={disabled}
        onChange={e => setText(e.target.value)}
      />
      <Button className="w-full" disabled={disabled || text.trim().length < 5} onClick={() => onSubmit({ text })}>
        Submit answer
      </Button>
    </div>
  );
}

export const responseRenderers: Record<string, (p: ResponseRendererProps) => JSX.Element> = {
  single_select: SelectOne,
  scenario_decision: SelectOne,
  error_identification: SelectOne,
  multi_select: SelectMany,
  numeric_response: NumericResponse,
  ordering: OrderingResponse,
  sequencing: OrderingResponse,
  matching: PairingResponse,
  categorization: PairingResponse,
  short_constructed_response: WrittenResponse,
  open_constructed_response: WrittenResponse,
  worked_response: WrittenResponse,
  transfer_application: WrittenResponse,
};

export function renderResponse(props: ResponseRendererProps) {
  const Renderer = responseRenderers[props.item.response_type] ?? SelectOne;
  return <Renderer {...props} />;
}
